import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Breadcrumbs, IconButton, lighten, Stack, Typography } from '@mui/material';
import { getCoPilotRouteForPathAndTab } from 'features/co-pilot/co-pilot-routes';
import { LinkedinTypes } from 'enums';
import { ContactsSearchStatuses, FileUpload, Search } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import PaiTooltip from 'features/ui/base-components/pai-tooltip';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';

interface FileItemProps {
  position: number;
  total: number;
  file: FileUpload;

  handleOpenDeleteContactsFileDialog(): void;
}

const FileItem: React.FC<FileItemProps> = ({ position, total, file, handleOpenDeleteContactsFileDialog }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <div className={classes.searchItem}>
      <Stack className={classes.searchItemHeader} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Breadcrumbs className={classes.breadcrumbs} separator={'|'}>
          <Typography className={classes.regularText}>{t('fileNumber', { position, total })}</Typography>
          <Typography className={classes.boldText}>{file.name}</Typography>
        </Breadcrumbs>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
          <IconButton size={'small'} onClick={handleOpenDeleteContactsFileDialog}>
            <DeleteIcon className={classes.icon} />
          </IconButton>
        </Stack>
      </Stack>
      <Stack className={classes.searchItemBody} gap={1}>
        <Typography className={classes.regularText}>
          <strong>{t('contacts')}:</strong> {file.contactCount || 0}
        </Typography>
      </Stack>
    </div>
  );
};

interface ItemProps {
  position: number;
  total: number;
  search: Search;

  mapLinkedinAccountIdToName: (linkedinAccountId: string) => string | undefined;

  handleOpenRenameContactSearchDialog(): void;

  handleOpenDeleteContactsSearchDialog(): void;

  handleOpenLoadContactsNumberDialog(): void;

  handleOpenEditSearchLinkedinAccountDialog(): void;
}

const SearchItem: React.FC<ItemProps> = ({
  position,
  total,
  search,
  mapLinkedinAccountIdToName,
  handleOpenRenameContactSearchDialog,
  handleOpenDeleteContactsSearchDialog,
  handleOpenLoadContactsNumberDialog,
  handleOpenEditSearchLinkedinAccountDialog,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const setActiveTab = useAppBarNavigationStore((state) => state.setActiveTab);

  const navigateToAccountManagement = () => {
    const route = getCoPilotRouteForPathAndTab(location.pathname, NavigationStates.LINKEDIN_ACCOUNT_MANAGEMENT);
    if (route) {
      navigate(route);
    } else {
      setActiveTab(NavigationStates.LINKEDIN_ACCOUNT_MANAGEMENT, false);
    }
  };

  const getStatus = () => {
    switch (search.status) {
      case ContactsSearchStatuses.RUNNING:
        return (
          <div className={cx(classes.status, 'running')}>
            <Typography>{t('loadingContacts')}</Typography>
          </div>
        );
      case ContactsSearchStatuses.PENDING:
        return (
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <div className={cx(classes.status, 'outOfHours')}>
              <Typography>{t('searchStatusOutOfHours')}</Typography>
            </div>
            <PaiHelpIcon tooltipText={t('nextBatchTooltip')} />
          </Stack>
        );
      case ContactsSearchStatuses.FINISHED:
        return (
          <div className={cx(classes.status, 'finished')}>
            <Typography>{t('finished')}</Typography>
          </div>
        );
      case ContactsSearchStatuses.QUOTA_EXCEEDED: {
        const retryAtText = search.nextRetryAt ? formatDateTime(search.nextRetryAt, 'Pp') : '';
        const tooltipText = retryAtText
          ? t('searchQuotaExceededTooltipWithDate', { retryAt: retryAtText })
          : t('searchQuotaExceededTooltip');
        return (
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <PaiLink
              text={t('searchStatusQuotaExceeded')}
              size={'lg'}
              variant={'primary'}
              underline={'always'}
              action={navigateToAccountManagement}
            />
            <PaiHelpIcon tooltipText={tooltipText} variant={'warning'} />
          </Stack>
        );
      }
      case ContactsSearchStatuses.RECRUITER_PARALLEL_LOGIN:
        return (
          <div className={cx(classes.status, 'warning')}>
            <Typography>{t('parallelLogin')}</Typography>
          </div>
        );
      case ContactsSearchStatuses.ACCOUNT_NOT_FOUND:
        return (
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <PaiLink
              text={t('accountNotFound')}
              size={'lg'}
              variant={'primary'}
              underline={'always'}
              action={handleOpenEditSearchLinkedinAccountDialog}
            />
            <PaiHelpIcon tooltipText={false} variant={'warning'} />
          </Stack>
        );
      case ContactsSearchStatuses.LINKEDIN_ACCOUNT_PROBLEM:
        return (
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <PaiLink
              text={t('linkedinAccountProblem')}
              size={'lg'}
              variant={'primary'}
              underline={'always'}
              action={navigateToAccountManagement}
            />
            <PaiHelpIcon tooltipText={false} variant={'warning'} />
          </Stack>
        );
      case ContactsSearchStatuses.SYNCHRONIZING_LINKEDIN_ACCOUNT:
        return (
          <div className={cx(classes.status, 'warning')}>
            <Typography>{t('linkedinAccountLoginInProgress')}</Typography>
          </div>
        );
      default:
        return <div className={classes.status}>{search.status}</div>;
    }
  };

  const mapLiProductType = (product: LinkedinTypes | undefined) => {
    switch (product) {
      case LinkedinTypes.LINKEDIN:
        return 'LinkedIn';

      case LinkedinTypes.SALES_NAV:
        return 'Sales Navigator';

      case LinkedinTypes.RECRUITER:
        return 'Recruiter';

      default:
        return 'LinkedIn';
    }
  };

  const savedContacts = search.saved ?? search.state?.crawled;
  const withheldProfiles = search.state?.withheld ?? 0;
  const progress = savedContacts
    ? `${savedContacts} / ${search.state?.total ?? '∞'} ${t('candidatesAdded')}`
    : search.status === 'finished'
    ? t('noContactsFound')
    : t('notStartedYet');

  const linkedinAccountName = mapLinkedinAccountIdToName(search.linkedinAccount);
  // A finished search can be run again to pick up profiles the first pass could not
  // add — nothing already in the campaign is touched, so this is a top-up, not a restart.
  const isFinishedSearch = search.status === ContactsSearchStatuses.FINISHED;
  const isStuckLoadingSearch = search.status === ContactsSearchStatuses.RUNNING;
  const canContinueSearch =
    search.status === ContactsSearchStatuses.PENDING ||
    search.status === ContactsSearchStatuses.QUOTA_EXCEEDED ||
    isFinishedSearch ||
    isStuckLoadingSearch;
  const continueSearchButtonText = isFinishedSearch
    ? t('reloadSearch')
    : isStuckLoadingSearch
    ? t('retryStuckSearch')
    : search.status === ContactsSearchStatuses.QUOTA_EXCEEDED
    ? t('continueSearch')
    : t('loadMore');
  const continueSearchTooltip = isFinishedSearch
    ? t('reloadSearchTooltip')
    : isStuckLoadingSearch
    ? t('retryStuckSearchTooltip')
    : t('loadMoreTooltip');

  return (
    <div className={classes.searchItem}>
      <Stack className={classes.searchItemHeader} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Breadcrumbs className={classes.breadcrumbs} separator={'|'}>
          <Typography className={classes.regularText}>{t('searchNumber', { position, total })}</Typography>
          <PaiLink
            text={_.truncate(search.name || '...', { length: 100 })}
            size={'lg'}
            bold={true}
            IconComponent={<EditIcon />}
            iconPlacement={'end'}
            showIconOnHover={true}
            variant={'primaryText'}
            underline={'never'}
            action={handleOpenRenameContactSearchDialog}
          />
        </Breadcrumbs>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
          <IconButton size={'small'} onClick={handleOpenRenameContactSearchDialog}>
            <EditIcon className={classes.icon} />
          </IconButton>
          <IconButton size={'small'} onClick={handleOpenDeleteContactsSearchDialog}>
            <DeleteIcon className={classes.icon} />
          </IconButton>
        </Stack>
      </Stack>
      <Stack className={classes.searchItemBody} gap={1}>
        <Breadcrumbs className={classes.breadcrumbs} separator={'|'}>
          {search.createDate ? <Typography className={classes.regularText}>{formatDateTime(search.createDate, 'Pp')}</Typography> : null}
          {search.liProductType ? (
            <Typography className={classes.regularText}>
              <strong>{t('source')}:</strong> {mapLiProductType(search.liProductType)}
            </Typography>
          ) : null}
          {linkedinAccountName ? (
            <Typography className={classes.regularText}>
              <strong>{t('linkedinAccount')}:</strong> {linkedinAccountName}
            </Typography>
          ) : null}
          <Typography className={classes.regularText}>
            <strong>{t('typeOfSearch')}:</strong> {t(search.type)}
          </Typography>
          {search.url ? (
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <Typography className={classes.regularText}>
                <strong>{t('linkedInSearchUrl')}:</strong>
              </Typography>
              <PaiLink text={t('openOnLinkedIn')} size={'lg'} variant={'primary'} underline={'always'} href={search.url} target={'_blank'} />
            </Stack>
          ) : null}
        </Breadcrumbs>
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          <Typography className={classes.regularText}>
            <strong>{t('status')}:</strong>
          </Typography>
          {getStatus()}
        </Stack>
        {search.status === ContactsSearchStatuses.QUOTA_EXCEEDED ? (
          <Typography className={classes.greyText}>{search.error || t('searchQuotaExceededHint')}</Typography>
        ) : null}
        <Breadcrumbs className={classes.breadcrumbs} separator={'|'}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <Typography className={classes.regularText}>
              <strong>{t('progress')}:</strong>
            </Typography>
            {progress}
          </Stack>
          {canContinueSearch ? (
            <PaiTooltip title={continueSearchTooltip}>
              <span>
                <PaiButton text={continueSearchButtonText} size={'small'} border={'round'} action={handleOpenLoadContactsNumberDialog} />
              </span>
            </PaiTooltip>
          ) : null}
        </Breadcrumbs>
        {withheldProfiles > 0 ? (
          <Stack direction={'row'} alignItems={'center'} gap={0.5}>
            <Typography className={classes.greyText}>{t('searchWithheldProfiles', { count: withheldProfiles })}</Typography>
            <PaiHelpIcon tooltipText={t('searchWithheldProfilesTooltip')} variant={'information'} size={'sm'} />
          </Stack>
        ) : null}
      </Stack>
    </div>
  );
};

interface ListProps {
  searches: Search[] | FileUpload[];
  searchesLoading: boolean;
  linkedinAccounts: LinkedinAccount[];

  handleOpenRenameContactSearchDialog(search: Search): () => void;

  handleOpenDeleteContactsSearchDialog(search: Search): () => void;

  handleOpenDeleteContactsFileDialog(file: FileUpload): () => void;

  handleOpenLoadContactsNumberDialog(search: Search): () => void;

  handleOpenEditSearchLinkedinAccountDialog(search: Search): () => void;

  handleMoveToNewSearch(): void;
}

const ManageSearches: React.FC<ListProps> = ({
  searches,
  searchesLoading,
  linkedinAccounts,
  handleOpenRenameContactSearchDialog,
  handleOpenDeleteContactsSearchDialog,
  handleOpenDeleteContactsFileDialog,
  handleOpenLoadContactsNumberDialog,
  handleOpenEditSearchLinkedinAccountDialog,
  handleMoveToNewSearch,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const filteredSearches = useMemo(() => {
    return searches.filter((search) => search.type !== 'file') as Search[];
  }, [searches]);

  const filteredFiles = useMemo(() => {
    return searches.filter((search) => search.type === 'file') as FileUpload[];
  }, [searches]);

  const generateTooltipText = () => {
    return (
      <Stack>
        <Typography className={classes.regularTooltipText} fontWeight={'bold'}>
          {t('noteAiDoesWork')}
        </Typography>
        <Typography className={classes.regularTooltipText} mb={'16px'}>
          {t('contactsLoadAutomatically')}
        </Typography>
        <Typography className={classes.regularTooltipText} mb={'16px'}>
          <Trans i18nKey={'searchAutomaticLoadingExplanation'}>
            <strong>Automatic loading:</strong> Contacts will add up as needed for the campaign. This process happens in the background once a
            campaign is active and the daily limit for loading contacts has not yet been reached.
          </Trans>
        </Typography>
        <Typography className={classes.regularTooltipText}>
          <Trans i18nKey={'searchSpecifiedLoadingExplanation'}>
            <strong>Specific number set:</strong> We will load contacts from the search until the specified number is reached or the search is
            finished. (Numbers can differ depending on contacts available from the crawling LinkedIn account)
          </Trans>
        </Typography>
      </Stack>
    );
  };

  const mapLinkedinAccountIdToName = (linkedinAccountId: string | undefined) => {
    if (!linkedinAccountId) {
      return undefined;
    }

    const linkedinAccount: LinkedinAccount | undefined = linkedinAccounts.find((account) => account._id === linkedinAccountId);
    return linkedinAccount?.userEmail || undefined;
  };

  return (
    <Stack className={classes.container} direction={'column'} gap={3}>
      {!_.isEmpty(searches) ? (
        <Stack direction={'row'} alignItems={'center'} pl={'4px'} gap={1}>
          <Typography className={classes.regularText}>{t('searchLoading')}</Typography>
          <PaiHelpIcon tooltipText={generateTooltipText()} />
        </Stack>
      ) : null}
      <Stack className={classes.listContent} gap={1}>
        {searchesLoading ? (
          <PaiDotLoader />
        ) : !_.isEmpty(searches) ? (
          <>
            {filteredSearches.map((search, index) => (
              <SearchItem
                key={index}
                position={index + 1}
                total={filteredSearches.length}
                search={search}
                mapLinkedinAccountIdToName={mapLinkedinAccountIdToName}
                handleOpenRenameContactSearchDialog={handleOpenRenameContactSearchDialog(search)}
                handleOpenDeleteContactsSearchDialog={handleOpenDeleteContactsSearchDialog(search)}
                handleOpenLoadContactsNumberDialog={handleOpenLoadContactsNumberDialog(search)}
                handleOpenEditSearchLinkedinAccountDialog={handleOpenEditSearchLinkedinAccountDialog(search)}
              />
            ))}
            {filteredFiles.map((file, index) => (
              <FileItem
                key={index}
                position={index + 1}
                total={filteredFiles.length}
                file={file}
                handleOpenDeleteContactsFileDialog={handleOpenDeleteContactsFileDialog(file)}
              />
            ))}
          </>
        ) : (
          <Stack alignItems={'center'} justifyContent={'center'} gap={3} sx={{ height: '100%', width: '100%' }}>
            <Typography className={classes.placeholderText}>{t('campaignNoSearchesPlaceholder')}</Typography>
            <PaiButton text={t('addNewSearch')} border={'rounded'} action={handleMoveToNewSearch} />
          </Stack>
        )}
      </Stack>
      {!_.isEmpty(searches) ? (
        <Stack direction={'row'} sx={{ marginTop: 'auto' }}>
          <PaiButton text={t('addNewSearch')} size={'small'} border={'rounded'} action={handleMoveToNewSearch} />
        </Stack>
      ) : null}
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
  },
  listContent: {
    height: '100%',
    overflow: 'auto',
  },
  searchItem: {
    position: 'relative',
    width: '100%',
    background: '#FFF',
    borderRadius: '12px',
  },
  breadcrumbs: {
    color: theme.palette.text.primary,
    '& .MuiBreadcrumbs-separator': {
      marginLeft: '12px',
      marginRight: '12px',
    },
  },
  searchItemHeader: {
    padding: '8px 32px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  searchItemBody: {
    padding: '16px 32px',
  },
  regularText: {
    fontSize: theme.typography.pxToRem(15),
  },
  boldText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  regularTooltipText: {
    fontSize: theme.typography.pxToRem(13),
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  placeholderText: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.greys.medium,
    fontWeight: 'bold',
  },
  status: {
    padding: '0 8px',
    borderRadius: '16px',
    background: lighten(theme.palette.greys.medium, 0.8),
    width: 'fit-content',
    '& .MuiTypography-root': {
      fontSize: theme.typography.pxToRem(15),
    },
    '&.running': {
      border: `1px solid ${theme.palette.backgrounds.success}`,
      background: theme.palette.backgrounds.lightGreen,
    },
    '&.outOfHours': {
      border: `1px solid ${theme.palette.backgrounds.warning}`,
      background: theme.palette.backgrounds.lightOrange,
    },
    '&.warning': {
      border: `1px solid ${theme.palette.backgrounds.warning}`,
      background: theme.palette.backgrounds.warning,
    },
    '&.finished': {
      border: `1px solid ${theme.palette.greys.light}`,
      background: theme.palette.backgrounds.lightViolet,
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.greys.medium,
  },
}));

export default ManageSearches;
